import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { appLogin, appRegister, appMe, appLogout, setAppToken, getAppToken, appAdminPassword } from '@/lib/appApi';

const AppAuthContext = createContext(null);

// Keep `sessionStorage.app_password` in sync with the admin's JWT session so
// the legacy `/listings` admin UI (which still uses the master password for
// its mutation endpoints) stays unlocked automatically after JWT login.
async function syncMasterPassword(u) {
  if (!u || u.app_role !== 'admin') {
    if (typeof window !== 'undefined') sessionStorage.removeItem('app_password');
    return;
  }
  try {
    const { password } = await appAdminPassword();
    if (password && typeof window !== 'undefined') {
      sessionStorage.setItem('app_password', password);
    }
  } catch { /* admin-only endpoint may 403 if token is stale */ }
}

/**
 * Provides the currently signed-in dashboard user. `user === undefined` means
 * we're still bootstrapping (token verify in flight); `null` means anonymous.
 */
export function AppAuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    if (!getAppToken()) { setUser(null); await syncMasterPassword(null); return; }
    try {
      const u = await appMe();
      setUser(u);
      await syncMasterPassword(u);
    } catch {
      setAppToken(null);
      setUser(null);
      await syncMasterPassword(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { token, user: u } = await appLogin(email, password);
    setAppToken(token); setUser(u);
    await syncMasterPassword(u);
    return u;
  };
  const register = async (name, email, password, pin) => {
    const { token, user: u } = await appRegister(name, email, password, pin);
    setAppToken(token); setUser(u);
    await syncMasterPassword(u);
    return u;
  };
  const logout = async () => {
    try { await appLogout(); } catch { /* ignore */ }
    setAppToken(null); setUser(null);
    await syncMasterPassword(null);
  };
  const updateMe = (next) => setUser((prev) => ({ ...(prev || {}), ...next }));

  const isAdmin = (user || {}).app_role === 'admin';
  const hasPage = (slug) => isAdmin || ((user || {}).app_pages || []).includes(slug);

  return (
    <AppAuthContext.Provider value={{ user, isAdmin, hasPage, login, register, logout, refresh, updateMe }}>
      {children}
    </AppAuthContext.Provider>
  );
}

export function useAppAuth() {
  const ctx = useContext(AppAuthContext);
  if (!ctx) throw new Error('useAppAuth must be used inside <AppAuthProvider>');
  return ctx;
}
